import React, { RefObject, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { NodeInstance } from '@glueapp/graph-types';
import { GetVariable, NODE_LIBRARY_DICTIONARY, SetVariable } from '@glueapp/graph-nodes';
import { PortOverlayContainer } from './PortOverlayContainer';
import { getColorFromDataType } from '@/GraphEditor/helpers/getColorFromDataType';
import type { NodePortPositions } from '../GraphEditorDrawing';
import type { PortOverlaySide, PortOverlayState } from './portOverlayTypes';
import { worldToScreenCoordinate } from '@/GraphEditor/drawingLayer/helpers/coordinateSystemMapping';
import { ThemeContext } from 'styled-components';
import type { ThemeType } from '@/theme/glueTheme';

const EDGE_MARGIN = 12;
const GAP = 8;

interface PortEntry {
    portName: string;
    portSide: PortOverlaySide;
}

function getOrderedPorts(nodeInstance: NodeInstance): PortEntry[] {
    const node = NODE_LIBRARY_DICTIONARY[nodeInstance.nodeId];
    if (!node) return [];

    const entries: PortEntry[] = [];

    // Special ports first
    if (node.integration) {
        entries.push({ portName: '__integration__', portSide: 'special' });
    }
    if (node.triggerType === 'Timer') {
        entries.push({ portName: '__schedule__', portSide: 'special' });
    }
    if (node.triggerType === 'Webhook') {
        entries.push({ portName: '__webhook__', portSide: 'special' });
    }
    if (node.id === GetVariable.id || node.id === SetVariable.id) {
        entries.push({ portName: '__variable__', portSide: 'special' });
    }

    // In ports
    for (const port of nodeInstance.inPorts) {
        entries.push({ portName: port.name, portSide: 'in' });
    }

    // Out ports
    for (const port of nodeInstance.outPorts) {
        entries.push({ portName: port.name, portSide: 'out' });
    }

    return entries;
}

interface Props {
    containerRef: RefObject<HTMLDivElement>;
    zoomLevel: number;
    nodePortPositions: NodePortPositions;
    nodeInstances: NodeInstance[];
    activeOverlay: PortOverlayState | null;
    openOverlay: (nodeInstanceId: number, portName: string, portSide: PortOverlaySide) => void;
    closeOverlay: () => void;
    renderOverlayContent: (
        nodeInstance: NodeInstance,
        portName: string,
        portSide: PortOverlaySide,
        closeOverlay: () => void,
    ) => React.ReactNode;
    theme: ThemeType;
}

const PortOverlayLayerInner: React.FC<Props & { nodeInstance: NodeInstance; layerRef: RefObject<HTMLDivElement> }> = ({
    containerRef,
    zoomLevel,
    nodePortPositions,
    nodeInstance,
    layerRef,
    activeOverlay,
    openOverlay,
    closeOverlay,
    renderOverlayContent,
    theme,
}) => {
    const navigateToPort = useCallback(
        (direction: 'next' | 'prev') => {
            if (!activeOverlay) {
                closeOverlay();
                return;
            }

            const ports = getOrderedPorts(nodeInstance);
            const currentIndex = ports.findIndex(p => p.portName === activeOverlay.portName);
            const nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;

            if (nextIndex < 0 || nextIndex >= ports.length) {
                closeOverlay();
                return;
            }

            const nextPort = ports[nextIndex];
            openOverlay(nodeInstance.id, nextPort.portName, nextPort.portSide);
        },
        [activeOverlay, nodeInstance, openOverlay, closeOverlay],
    );
    const overlayRef = useRef<HTMLDivElement>(null);
    const [scrollOffset, setScrollOffset] = useState({ x: 0, y: 0 });
    const [adjust, setAdjust] = useState({ x: 0, y: 0 });
    const [overlaySize, setOverlaySize] = useState({ width: 0, height: 0 });
    const adjustRef = useRef(adjust);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const updateScroll = () => {
            setScrollOffset({ x: container.scrollLeft, y: container.scrollTop });
        };

        updateScroll();

        container.addEventListener('scroll', updateScroll, { passive: true });
        return () => container.removeEventListener('scroll', updateScroll);
    }, [containerRef]);

    useEffect(() => {
        adjustRef.current = adjust;
    }, [adjust]);

    useEffect(() => {
        const el = overlayRef.current;
        if (el) {
            const focusable = el.querySelector<HTMLElement>('input, textarea, select, [tabindex]');
            if (focusable) {
                requestAnimationFrame(() => focusable.focus());
            }
        }
    }, [activeOverlay?.portName]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Tab') {
                e.preventDefault();
                navigateToPort(e.shiftKey ? 'prev' : 'next');
                return;
            }

            if (e.key === 'Enter' && !e.shiftKey && activeOverlay?.portSide !== 'out') {
                const target = e.target as HTMLElement;
                if (!target.closest('form')) {
                    e.preventDefault();
                    closeOverlay();
                }
            }

            if (e.key === 'Escape') {
                e.preventDefault();
                closeOverlay();
            }
        },
        [navigateToPort, closeOverlay],
    );

    // Reset adjustment when overlay changes so we re-measure from base position
    const portName = activeOverlay?.portName;
    const nodeInstanceId = activeOverlay?.nodeInstanceId;

    useLayoutEffect(() => {
        if (adjustRef.current.x !== 0 || adjustRef.current.y !== 0) {
            setAdjust({ x: 0, y: 0 });
        }
    }, [portName, nodeInstanceId]);

    useLayoutEffect(() => {
        const overlay = overlayRef.current;
        if (!overlay) return;

        const observer = new ResizeObserver(entries => {
            const entry = entries[0];
            if (!entry) return;
            const { width, height } = entry.contentRect;
            setOverlaySize(prev => (prev.width === width && prev.height === height ? prev : { width, height }));
        });

        observer.observe(overlay);
        return () => observer.disconnect();
    }, [portName, nodeInstanceId]);

    // After rendering at ideal position, measure and shift if off-screen
    useLayoutEffect(() => {
        const overlay = overlayRef.current;
        const layer = layerRef.current;
        if (!overlay || !layer) return;

        const overlayRect = overlay.getBoundingClientRect();
        const layerRect = layer.getBoundingClientRect();

        const baseLeft = overlayRect.left - adjustRef.current.x;
        const baseRight = overlayRect.right - adjustRef.current.x;
        const baseTop = overlayRect.top - adjustRef.current.y;
        const baseBottom = overlayRect.bottom - adjustRef.current.y;

        let dx = 0;
        let dy = 0;

        // Shift left if overflowing right edge
        if (baseRight > layerRect.right - EDGE_MARGIN) {
            dx = layerRect.right - EDGE_MARGIN - baseRight;
        }
        // Shift right if overflowing left edge
        if (baseLeft + dx < layerRect.left + EDGE_MARGIN) {
            dx = layerRect.left + EDGE_MARGIN - baseLeft;
        }

        // Shift up if overflowing bottom edge
        if (baseBottom > layerRect.bottom - EDGE_MARGIN) {
            dy = layerRect.bottom - EDGE_MARGIN - baseBottom;
        }
        // Shift down if overflowing top edge
        if (baseTop + dy < layerRect.top + EDGE_MARGIN) {
            dy = layerRect.top + EDGE_MARGIN - baseTop;
        }

        if (dx !== adjustRef.current.x || dy !== adjustRef.current.y) {
            setAdjust({ x: dx, y: dy });
        }
    }, [overlaySize.width, overlaySize.height, portName, nodeInstanceId, scrollOffset.x, scrollOffset.y, zoomLevel]);

    if (!activeOverlay) return null;

    const portPositions = nodePortPositions[activeOverlay.nodeInstanceId];
    if (!portPositions) return null;

    const isLeft = activeOverlay.portSide === 'in' || activeOverlay.portSide === 'special';
    const canvasPos = isLeft
        ? portPositions.inPortPositions[activeOverlay.portName]
        : portPositions.outPortPositions[activeOverlay.portName];

    if (!canvasPos) return null;

    const screenPos = worldToScreenCoordinate(canvasPos, scrollOffset, zoomLevel);

    let portColor: string | undefined;
    if (!activeOverlay.portName.startsWith('__')) {
        const ports = activeOverlay.portSide === 'out' ? nodeInstance.outPorts : nodeInstance.inPorts;
        const port = ports?.find(p => p.name === activeOverlay.portName);
        if (port?.type) {
            portColor = getColorFromDataType(port.type, theme);
        }
    }

    // For out ports, shift the anchor to the right of the indicator button
    // so the overlay appears at the same position as the hover preview.
    // The indicator is positioned 12px right of the port edge and is 16px wide (in node-space),
    // plus a 4px gap — totalling 32px scaled by zoomLevel.
    const outPortIndicatorOffset = activeOverlay.portSide === 'out' ? 32 * zoomLevel : 0;
    const anchorX = isLeft ? screenPos.x - GAP : screenPos.x + GAP + outPortIndicatorOffset;

    const basePositionStyle: React.CSSProperties = {
        position: 'absolute',
        top: screenPos.y - overlaySize.height / 2,
        transform: `translate(${adjust.x}px, ${adjust.y}px)`,
        pointerEvents: 'all',
        maxWidth: `calc(100% - ${EDGE_MARGIN * 2}px)`,
        maxHeight: `calc(100% - ${EDGE_MARGIN * 2}px)`,
    };

    const positionStyle: React.CSSProperties = isLeft
        ? {
              ...basePositionStyle,
              right: `calc(100% - ${anchorX}px)`,
          }
        : {
              ...basePositionStyle,
              left: anchorX,
          };

    return (
        <div ref={overlayRef} onKeyDown={handleKeyDown} style={positionStyle}>
            <PortOverlayContainer portSide={activeOverlay.portSide} portColor={portColor}>
                {renderOverlayContent(nodeInstance, activeOverlay.portName, activeOverlay.portSide, closeOverlay)}
            </PortOverlayContainer>
        </div>
    );
};

export const PortOverlayLayer: React.FC<Omit<Props, 'theme'>> = props => {
    const layerRef = useRef<HTMLDivElement>(null);
    const theme = useContext<ThemeType>(ThemeContext);

    if (!props.activeOverlay) return null;

    const nodeInstance = props.nodeInstances.find(ni => ni.id === props.activeOverlay?.nodeInstanceId);
    if (!nodeInstance) return null;

    return (
        <div
            ref={layerRef}
            style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                overflow: 'hidden',
            }}
        >
            <PortOverlayLayerInner {...props} theme={theme} nodeInstance={nodeInstance} layerRef={layerRef} />
        </div>
    );
};
